import { PropertiesStore } from '@hortplus/properties-react';
import { useFormik } from 'formik';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import Select from 'react-select';
import * as yup from 'yup';
import { errorToast } from '../../helpers/ToastUtils';
import { useStores } from '../../stores/useStores';

const SeasonOverviewReportForm = (props) => {
  const { traplogStore } = useStores();
  const [loadingPropertyGroups, setLoadingPropertyGroups] = useState(false);

  const propertyGroups = props.propertyGroups;
  const setPropertyGroups = props.setPropertyGroups;
  const propertyId = props.propertyId;

  const customInsects = ['CM', 'CM SIT', 'LBAM', 'LBAM SIT'];

  const propertyRunsOptions = useMemo(
    () =>
      propertyGroups
        .filter(
          (propertyGroup) =>
            !propertyGroup.deleted_at &&
            propertyGroup.group_type === 'trapping_run'
        )
        .map((propertyGroup) => {
          return {
            value: propertyGroup.id,
            label: propertyGroup.name,
            custom: propertyGroup
          };
        }),
    [propertyGroups]
  );

  const propertyGroupsOptions = useMemo(
    () =>
      propertyGroups
        .filter(
          (propertyGroup) =>
            !propertyGroup.deleted_at && propertyGroup.group_type === 'manual'
        )
        .map((propertyGroup) => {
          return {
            value: propertyGroup.id,
            label: propertyGroup.name,
            custom: propertyGroup
          };
        }),
    [propertyGroups]
  );

  const formik = useFormik({
    initialValues: {
      propertyGroup: props.selectedPropertyGroup,
      insectGroup: props.selectedInsectGroup
    },
    validationSchema: yup.object().shape({
      insectGroup: yup.object().required()
    }),
    onSubmit: async (values) => {
      props.setLoading(true);
      props.setSelectedInsectGroup(values.insectGroup);
      props.setSelectedPropertyGroup(values.propertyGroup);
      traplogStore.selectedInsectGroupId = values.insectGroup.id;

      const properties = [];
      await Promise.all(
        [values.propertyGroup].map((propertyGroup) => {
          return fetch(
            `${process.env.REACT_APP_TRAPLOG_API_URL}api/property-groups/${propertyGroup.id}?with[]=properties.trappingZones.traps.trapLure.insects&with[]=properties.trappingZones.matingDisruptionTie`,
            {
              headers: {
                Authorization: `Bearer ${props.bearerToken}`,
                Accept: 'application/json',
                SeasonalDatabase: props.selectedDatabase
              }
            }
          )
            .then((res) => {
              if (res.ok) return res.json();
              else if (res.status === 401) traplogStore.refreshBearerToken();
              else throw Error(res.statusText);
            })
            .then((data) => {
              data.properties.map((property) => {
                if (!properties.find((p) => p.id === property.id))
                  properties.push(property);
              });
            })
            .catch((err) => {
              if (process.env.NODE_ENV === 'development') console.error(err);
            });
        })
      );

      props.setSelectedProperties(properties);
      props.setLoading(false);
    }
  });

  /**
   * Load insect groups and property groups
   */
  useEffect(() => {
    // Fetch insect groups.
    if (traplogStore.insectGroups.length === 0) {
      traplogStore.loadInsectGroups(
        props.bearerToken,
        props.refreshBearerToken
      );
    }
    // Fetch property groups.
    if (!props.selectedDatabase) return;
    if (propertyGroups.length > 0) return;
    setLoadingPropertyGroups(true);
    fetch(
      `${process.env.REACT_APP_PROPERTIES_API_URL}api/property-groups?with[]=properties`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${props.bearerToken}`,
          SeasonalDatabase: props.selectedDatabase
        }
      }
    )
      .then((res) => {
        if (res.ok) return res.json();
        else if (res.status === 401) traplogStore.refreshBearerToken();
        else throw Error(res.statusText);
      })
      .then((data) => {
        if (data) {
          setPropertyGroups(data);
        }
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development') console.error(err);
        setPropertyGroups([]);
      })
      .finally(() => {
        setLoadingPropertyGroups(false);
      });
  }, [props.selectedDatabase, props.bearerToken, propertyId]);

  useEffect(() => {
    if (propertyGroupsOptions.length === 0) return;
    if (formik.values.propertyGroup) return;
    let foundProperty = false;
    for (let propertyGroup of propertyGroups) {
      const properties = propertyGroup.properties;
      if (!properties) continue;
      const found = properties.find((p) => p.id === propertyId);
      if (!found) continue;
      const option = propertyGroupsOptions.find((pgo) => {
        return pgo.value === propertyGroup.id;
      });
      if (!option) continue;
      foundProperty = true;
      props.setSelectedPropertyGroup(propertyGroup);
      formik.setFieldValue('propertyGroup', propertyGroup);
      break;
    }
    if (propertyId && !foundProperty) {
      errorToast('Property not found', 'property-not-found');
    }
  }, [propertyGroups, propertyGroupsOptions]);

  return (
    <Form onSubmit={formik.handleSubmit}>
      <Row className='mb-2 justify-content-center'>
        <Col
          xs={3}
          sm={2}
          className='d-flex align-items-center justify-content-end pr-0 font-weight-bold'
        >
          Trapping Run:
        </Col>
        <Col xs={9} md={8} lg={6}>
          <Select
            value={
              props.selectedPropertyGroup
                ? {
                    value: props.selectedPropertyGroup.id,
                    label: props.selectedPropertyGroup.name,
                    custom: props.selectedPropertyGroup
                  }
                : null
            }
            placeholder='Select a trapping run or property group'
            options={[
              {
                label: 'Trapping Runs',
                options: propertyRunsOptions
              },
              {
                label: 'Property Groups',
                options: propertyGroupsOptions
              }
            ]}
            isLoading={loadingPropertyGroups}
            onChange={(option) => {
              props.setSelectedPropertyGroup(option.custom);
              formik.setFieldValue('propertyGroup', option.custom);
            }}
          />
        </Col>
        <Col
          xs={0}
          sm={1}
          className='d-flex align-items-center justify-content-end pl-0 font-weight-bold'
          style={{ visibility: 'hidden' }}
        >
          Trapping Run:
        </Col>
      </Row>
      <Row className='justify-content-center'>
        <Col
          xs={3}
          sm={2}
          className='d-flex align-items-center justify-content-end pr-0 font-weight-bold'
        >
          Insect:
        </Col>
        <Col xs={9} md={8} lg={6}>
          <Select
            defaultValue={
              props.selectedInsectGroup
                ? {
                    value: props.selectedInsectGroup.id,
                    label: props.selectedInsectGroup.name,
                    custom: props.selectedInsectGroup
                  }
                : undefined
            }
            placeholder='Select an insect'
            isLoading={traplogStore.loadingInsectGroups}
            options={traplogStore.insectGroups
              .filter((value) => customInsects.includes(value.short_name))
              .map((insectGroup) => {
                return {
                  value: insectGroup.id,
                  label: insectGroup.name,
                  custom: insectGroup
                };
              })}
            onChange={(option) => {
              formik.setFieldValue('insectGroup', option.custom);
            }}
          />
        </Col>
        <Col
          xs={0}
          sm={1}
          className='d-flex align-items-center justify-content-end pl-0 font-weight-bold'
          style={{ visibility: 'hidden' }}
        >
          Insect:
        </Col>
      </Row>
      <Row className='mt-3 pb-1'>
        <Col className='text-center'>
          <Button
            variant='outline-primary'
            type='submit'
            disabled={
              formik.isSubmitting ||
              loadingPropertyGroups ||
              traplogStore.loadingInsectGroups
            }
          >
            Submit
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

SeasonOverviewReportForm.propTypes = {
  selectedDatabase: PropTypes.string,
  bearerToken: PropTypes.string.isRequired,
  propertiesStore: PropTypes.instanceOf(PropertiesStore).isRequired,
  setLoading: PropTypes.func.isRequired,
  setSelectedProperties: PropTypes.func.isRequired,
  selectedInsectGroup: PropTypes.object,
  setSelectedInsectGroup: PropTypes.func.isRequired,
  propertyId: PropTypes.number
};

export default observer(SeasonOverviewReportForm);
